import { apiClient } from './apiClient';
import { getToken, setToken, clearToken } from './tokenService';

/**
 * Sign in with existing credentials.
 *
 * Keyed on **email**, not username (D40): usernames are display names and are
 * not unique, so they can't identify an account.
 *
 * If a guest token was being held, that guest record is abandoned here - the
 * credentialed account wins (roadmap §3.1). Nothing is merged.
 */
export const login = async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    setToken(response.token);
    return response.user;
};

/**
 * Sign up.
 *
 * Under D22 this is credential *attachment*: the server sets a username and
 * password on the record the caller's guest token already points at, so
 * favorites, plays and friends carry over. Only a caller with no token gets a
 * brand new record.
 */
export const register = async (username, password, email) => {
    const response = await apiClient.post('/auth/register', {
        username,
        password,
        email
    });
    setToken(response.token);
    return response.user;
};

/**
 * Ask for a password-reset email.
 *
 * Always resolves when the request itself succeeds, whatever the address was.
 * The server deliberately can't tell us whether an account matched — saying so
 * would make this a way to test which addresses have accounts — so callers
 * must show the same confirmation either way.
 */
export const requestPasswordReset = async (email) => {
    await apiClient.post('/auth/password-reset/request', { email });
};

/**
 * Spend a reset token and set a new password. Signs the user in on success:
 * they've just proved they control the account's email address.
 */
export const resetPassword = async (token, password) => {
    const response = await apiClient.post('/auth/password-reset/consume', { token, password });
    setToken(response.token);
    return response.user;
};

export const logout = async () => {
    try {
        if (getToken()) {
            await apiClient.post('/auth/logout', {});
        }
    } catch (error) {
        // Logging out locally matters more than the server acknowledging it.
        console.log('Error notifying the server of logout:', error);
    } finally {
        clearToken();
    }
};

/**
 * Restore the session from a stored token.
 *
 * Returns the user for *any* valid token, guest records included - under D22
 * a guest is a real user, just one with no credentials attached. Callers
 * distinguish the two with `is_guest`.
 */
export const checkAutoLogin = async () => {
    if (!getToken()) return null;

    try {
        const response = await apiClient.get('/auth/profile');
        return {
            id: response.id,
            username: response.username,
            created_at: response.created_at,
            is_guest: response.is_guest
        };
    } catch {
        // Never clear the token here - only an explicit logout() should do
        // that. A transient failure must not orphan the account.
        return null;
    }
};
